import React, { useState } from "react";
import { Tabs, Tab, Box, Typography, Table, TableBody, TableRow, TableCell } from "@mui/material";
import PropTypes from "prop-types";
import { CustomTableCell } from "components/StyledComponents/styledComponents";

const DisbursementTabs = ({ disbursements }) => {
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <>
      <Tabs value={activeTab} onChange={handleTabChange} aria-label="Disbursement Tabs">
        {disbursements.map((disbursement, index) => (
          <Tab
            key={index}
            label={`Disbursement ${index + 1}`}
            sx={{
              color: disbursement.disbursementStatus === "D" ? "rgba(23, 3, 3, 0.84)" : "#ffffff",
              backgroundColor:
                disbursement.disbursementStatus === "D" ? "rgba(255, 0, 0, 0.8)" : "#ffffff",
            }}
          />
        ))}
      </Tabs>
      {disbursements.map((disbursement, index) => (
        <TabPanel key={index} value={activeTab} index={index}>
          <DisbursementDetails disbursement={disbursement} />
        </TabPanel>
      ))}
    </>
  );
};

DisbursementTabs.propTypes = {
  disbursements: PropTypes.arrayOf(
    PropTypes.shape({
      appliedAmount: PropTypes.number.isRequired,
      disbursedAmount: PropTypes.number.isRequired,
      grossPayoutAmount: PropTypes.number.isRequired,
      grossPayoutPercent: PropTypes.number.isRequired,
      dsaPayoutPercent: PropTypes.number.isRequired,
      dsaPayoutAmount: PropTypes.number.isRequired,
      subDSAPayoutPercent: PropTypes.number.isRequired,
      subDSAPaymentAmount: PropTypes.number.isRequired,
      dsaMargin: PropTypes.number.isRequired,
      finalGrossPayoutAmount: PropTypes.number.isRequired,
      finalDSAGrossPayoutAmount: PropTypes.number.isRequired,
      finalSubDSAGrossPayoutAmount: PropTypes.number.isRequired,
      dsaTdsPercent: PropTypes.number.isRequired,
      entryId: PropTypes.number.isRequired,
    })
  ).isRequired,
};

const TabPanel = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
};

TabPanel.propTypes = {
  children: PropTypes.node,
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
};

const DisbursementDetails = ({ disbursement }) => {
  const {
    appliedAmount,
    disbursedAmount,
    grossPayoutAmount,
    grossPayoutPercent,
    dsaPayoutPercent,
    dsaPayoutAmount,
    subDSAPayoutPercent,
    subDSAPaymentAmount,
    dsaMargin,
    finalGrossPayoutAmount,
    finalDSAGrossPayoutAmount,
    finalSubDSAGrossPayoutAmount,
    dsaTdsPercent,
    entryId,
  } = disbursement;

  return (
    <Table>
      <TableBody>
        <TableRow>
          <CustomTableCell minWidth="100px" colSpan={2} flexGrow={1}>
            Disbursed Amount
          </CustomTableCell>
          <CustomTableCell minWidth="100px" flexGrow={1} fontWeight="bold" align="right">
            ₹
            {disbursedAmount.toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </CustomTableCell>
        </TableRow>
        <TableRow>
          <CustomTableCell minWidth="100px" flexGrow={1} fontWeight="normal">
            Banker Payout @ {Number(grossPayoutPercent).toFixed(2)} %
          </CustomTableCell>
          <CustomTableCell minWidth="100px" flexGrow={1} fontWeight="normal" align="right">
            ₹{" "}
            {`${(Number(grossPayoutAmount) || 0).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`}
          </CustomTableCell>
          <CustomTableCell minWidth="100px" flexGrow={1} fontWeight="normal"></CustomTableCell>
        </TableRow>
        <TableRow>
          <CustomTableCell minWidth="100px" flexGrow={1} fontWeight="normal">
            Less: TDS @ 2 %
          </CustomTableCell>
          <CustomTableCell minWidth="100px" flexGrow={1} fontWeight="normal" align="right">
            ₹{" "}
            {`${(Number(grossPayoutAmount - finalGrossPayoutAmount) || 0).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`}
          </CustomTableCell>
          <CustomTableCell minWidth="100px" flexGrow={1} fontWeight="normal"></CustomTableCell>
        </TableRow>
        <TableRow sx={{ backgroundColor: "rgba(51, 51, 51, 0.67)" }}>
          <CustomTableCell
            sx={{ color: "#ffffff" }}
            minWidth="100px"
            colSpan={2}
            flexGrow={1}
            fontWeight="bold"
          >
            Net Banker Payout
          </CustomTableCell>
          <CustomTableCell
            sx={{ color: "#ffffff" }}
            minWidth="100px"
            flexGrow={1}
            fontWeight="bold"
            align="right"
          >
            ₹{" "}
            {`${(Number(finalGrossPayoutAmount) || 0).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`}
          </CustomTableCell>
        </TableRow>
        {/* DSA Payout Row */}
        <TableRow>
          <CustomTableCell minWidth="100px" flexGrow={1} fontWeight="normal">
            DSA Payout @ {Number(dsaPayoutPercent).toFixed(2)} %
          </CustomTableCell>
          <CustomTableCell minWidth="100px" flexGrow={1} fontWeight="normal" align="right">
            ₹{" "}
            {`${(Number(dsaPayoutAmount) || 0).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`}
          </CustomTableCell>
          <CustomTableCell minWidth="100px" flexGrow={1} fontWeight="normal"></CustomTableCell>
        </TableRow>
        <TableRow
          sx={{
            height: "30px", // Set a smaller height
          }}
        >
          <CustomTableCell minWidth="100px" flexGrow={1} fontWeight="normal">
            Less : TDS @ {2} %
          </CustomTableCell>
          <CustomTableCell minWidth="100px" flexGrow={1} fontWeight="normal" align="right">
            ₹
            {` ${(Number(dsaPayoutAmount - finalDSAGrossPayoutAmount) || 0).toLocaleString(
              "en-IN",
              {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }
            )}`}
          </CustomTableCell>
          <CustomTableCell minWidth="100px" flexGrow={1} fontWeight="normal"></CustomTableCell>
        </TableRow>
        <TableRow sx={{ backgroundColor: "rgba(51, 51, 51, 0.67)" }}>
          <CustomTableCell
            sx={{ color: "#ffffff" }}
            minWidth="100px"
            colSpan={2}
            flexGrow={1}
            fontWeight="bold"
          >
            Net DSA Payout
          </CustomTableCell>
          <CustomTableCell
            sx={{ color: "#ffffff" }}
            minWidth="100px"
            flexGrow={1}
            fontWeight="bold"
            align="right"
          >
            ₹{" "}
            {`${(Number(finalDSAGrossPayoutAmount) || 0).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`}
          </CustomTableCell>
        </TableRow>
        {/* Connector Payout Row */}
        <TableRow>
          <CustomTableCell minWidth="100px" flexGrow={1} fontWeight="normal">
            Connector Payout @ {Number(subDSAPayoutPercent).toFixed(2)} %
          </CustomTableCell>
          <CustomTableCell minWidth="100px" flexGrow={1} fontWeight="normal" align="right">
            ₹{" "}
            {`${(Number(subDSAPaymentAmount) || 0).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`}
          </CustomTableCell>
          <CustomTableCell minWidth="100px" flexGrow={1} fontWeight="normal"></CustomTableCell>
        </TableRow>
        <TableRow
          sx={{
            height: "30px", // Set a smaller height
          }}
        >
          <CustomTableCell minWidth="100px" flexGrow={1} fontWeight="normal">
            Less : TDS @ {2} %
          </CustomTableCell>
          <CustomTableCell minWidth="100px" flexGrow={1} fontWeight="normal" align="right">
            ₹
            {` ${(Number(subDSAPaymentAmount - finalSubDSAGrossPayoutAmount) || 0).toLocaleString(
              "en-IN",
              {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }
            )}`}
          </CustomTableCell>
          <CustomTableCell minWidth="100px" flexGrow={1} fontWeight="normal"></CustomTableCell>
        </TableRow>
        {/* Net Connector Payout */}
        <TableRow sx={{ backgroundColor: "rgba(51, 51, 51, 0.67)" }}>
          <CustomTableCell
            sx={{ color: "#ffffff" }}
            minWidth="100px"
            colSpan={2}
            flexGrow={1}
            fontWeight="bold"
          >
            Net Connector Payout
          </CustomTableCell>
          <CustomTableCell
            sx={{ color: "#ffffff" }}
            minWidth="100px"
            flexGrow={1}
            fontWeight="bold"
            align="right"
          >
            ₹{" "}
            {`${(Number(finalSubDSAGrossPayoutAmount) || 0).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`}
          </CustomTableCell>
        </TableRow>
        <TableRow
          sx={{
            backgroundColor:
              Number(finalDSAGrossPayoutAmount - finalSubDSAGrossPayoutAmount) >= 0
                ? "rgba(51, 51, 51, 0.96)"
                : "red",
          }}
        >
          <CustomTableCell
            sx={{ color: "#ffffff" }}
            minWidth="100px"
            colSpan={2}
            flexGrow={1}
            fontWeight="bold"
          >
            {Number(finalDSAGrossPayoutAmount - finalSubDSAGrossPayoutAmount) > 0
              ? "Net DSA Profit / Loss"
              : "Net DSA Profit / Loss"}
          </CustomTableCell>
          <CustomTableCell
            sx={{ color: "#ffffff" }}
            minWidth="100px"
            flexGrow={1}
            fontWeight="bold"
            align="right"
          >
            ₹{" "}
            {`${(
              Number(finalDSAGrossPayoutAmount - finalSubDSAGrossPayoutAmount) || 0
            ).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`}
          </CustomTableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
};

DisbursementDetails.propTypes = {
  disbursement: PropTypes.shape({
    appliedAmount: PropTypes.number.isRequired,
    disbursedAmount: PropTypes.number.isRequired,
    grossPayoutAmount: PropTypes.number.isRequired,
    grossPayoutPercent: PropTypes.number.isRequired,
    dsaPayoutPercent: PropTypes.number.isRequired,
    dsaPayoutAmount: PropTypes.number.isRequired,
    subDSAPayoutPercent: PropTypes.number.isRequired,
    subDSAPaymentAmount: PropTypes.number.isRequired,
    dsaMargin: PropTypes.number.isRequired,
    finalGrossPayoutAmount: PropTypes.number.isRequired,
    finalDSAGrossPayoutAmount: PropTypes.number.isRequired,
    finalSubDSAGrossPayoutAmount: PropTypes.number.isRequired,
    dsaTdsPercent: PropTypes.number.isRequired,
    entryId: PropTypes.number.isRequired,
  }).isRequired,
};

export default DisbursementTabs;
